import { useEffect, useState } from "react";
import { LogOut, Plus, Save, Trash2, ArrowLeft, Briefcase, Pencil, EyeOff, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API } from "@/data/site";
import Logo from "@/components/Logo";

const TOKEN_KEY = "exoplusbio-admin-token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const authHeaders = (token) => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` });

function TeamSection({ token }) {
  const [members, setMembers] = useState([]);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    fetch(`${API}/api/team`).then((r) => r.json()).then(setMembers).catch(() => {});
  }, []);

  const refresh = () => fetch(`${API}/api/team`).then((r) => r.json()).then(setMembers).catch(() => {});

  const upload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/team/${key}/photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.status === 401) { window.location.reload(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      toast.success("Photo updated — live on the team page");
      refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading("");
    }
  };

  const remove = async (key) => {
    const res = await fetch(`${API}/api/admin/team/${key}/photo`, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    if (res.status === 401) { window.location.reload(); return; }
    toast.success("Photo removed");
    refresh();
  };

  return (
    <div className="mt-12 border border-[#e4e9ec] bg-white p-8" data-testid="admin-team-section">
      <h2 className="font-display text-2xl">Meet the Team — profile photos</h2>
      <p className="mt-2 text-sm text-[#56636b]">
        Upload a headshot for each team member (JPG or PNG, up to 4 MB). The photo appears on the public team page
        instantly; members without a photo show their initials.
      </p>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {members.map((m) => (
          <div key={m.member_key} className="flex items-center gap-4 border border-[#e4e9ec] p-4" data-testid={`admin-team-member-${m.member_key}`}>
            {m.photo_url ? (
              <img src={`${m.photo_url}?v=${Date.now()}`} alt={m.name} className="h-14 w-14 rounded-full border border-[#e4e9ec] object-cover" />
            ) : (
              <span className="font-display flex h-14 w-14 items-center justify-center rounded-full border border-[#e4e9ec] text-lg text-[#56636b]">
                {m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{m.name}</p>
              <label className="font-lab mt-1 block cursor-pointer text-[9px] uppercase tracking-[0.14em] text-[#0e8c8c] hover:underline">
                {uploading === m.member_key ? "Uploading…" : m.photo_url ? "Replace photo" : "Upload photo"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid={`admin-team-photo-input-${m.member_key}`}
                  onChange={(e) => upload(m.member_key, e.target.files?.[0])}
                />
              </label>
              {m.photo_url && (
                <button onClick={() => remove(m.member_key)} data-testid={`admin-team-remove-${m.member_key}`} className="font-lab mt-1 block text-[9px] uppercase tracking-[0.14em] text-[#b3261e] hover:underline">
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full border border-[#e4e9ec] px-4 py-3 text-sm outline-none transition-colors focus:border-[#0e8c8c]";

  return (
    <div className="lab-grid flex min-h-screen items-center justify-center px-5 pt-16" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm border border-[#e4e9ec] bg-white p-8 shadow-sm">
        <Logo />
        <h1 className="font-display mt-8 text-3xl tracking-tight">Admin access</h1>
        <p className="font-lab mt-2 text-[10px] uppercase tracking-[0.16em] text-[#56636b]">Pricing & product management</p>
        <div className="mt-8 space-y-4">
          <input type="text" required autoComplete="username" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)}
            data-testid="admin-login-username" className={input} />
          <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-login-password" className={input} />
        </div>
        {error && <p className="mt-4 border border-[#b3261e]/30 bg-[#b3261e]/5 px-3 py-2 text-xs text-[#b3261e]" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-submit"
          className="mt-6 w-full bg-[#0a0a0a] px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#0e8c8c] disabled:opacity-50">
          {busy ? "Verifying…" : "Sign in"}
        </button>
        <Link to="/" className="font-lab mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[#56636b] hover:text-[#0a0a0a]" data-testid="admin-back-link">
          <ArrowLeft className="h-3 w-3" /> Back to site
        </Link>
      </form>
    </div>
  );
}

const JOB_CATEGORIES = ["Professional Sales", "Laboratory Assistant", "Warehouse & Logistics", "Legal & Compliance", "Finance & Accounting"];
const JOB_STATUS_LABEL = { open: "OPEN", filled: "FILLED", hidden: "HIDDEN" };

function JobRowForm({ initial, busy, submitLabel, onSubmit, onCancel, testPrefix }) {
  const input = "w-full border border-[#e4e9ec] px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#0e8c8c]";
  const label = "font-lab mb-1.5 block text-[9px] uppercase tracking-[0.16em] text-[#56636b]";
  const [f, setF] = useState(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} data-testid={`${testPrefix}-form`} className="mt-4 border border-[#0e8c8c]/30 bg-[#f7f9f9]/60 p-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><label className={label}>Job title *</label>
          <input required minLength={3} className={input} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid={`${testPrefix}-title`} /></div>
        <div><label className={label}>Category *</label>
          <select className={input} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid={`${testPrefix}-category`}>
            {JOB_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><label className={label}>Status *</label>
          <select className={input} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid={`${testPrefix}-status`}>
            <option value="open">Open — accepting applications</option>
            <option value="filled">Filled — visible, marked as filled</option>
            <option value="hidden">Hidden — not shown publicly</option>
          </select></div>
        <div><label className={label}>Region *</label>
          <input required className={input} value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} data-testid={`${testPrefix}-region`} /></div>
        <div><label className={label}>Compensation</label>
          <input className={input} placeholder="e.g. Base salary + commission" value={f.compensation || ""} onChange={(e) => setF({ ...f, compensation: e.target.value })} data-testid={`${testPrefix}-compensation`} /></div>
        <div className="sm:col-span-2"><label className={label}>Description *</label>
          <textarea required rows={3} minLength={10} className={input} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid={`${testPrefix}-description`} /></div>
      </div>
      <div className="mt-4 flex gap-3">
        <button type="submit" disabled={busy} data-testid={`${testPrefix}-save`}
          className="bg-[#0e8c8c] px-8 py-3 text-sm font-medium text-white transition-colors hover:bg-[#0a0a0a] disabled:opacity-50">
          {busy ? "Saving…" : submitLabel}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} data-testid={`${testPrefix}-cancel`}
            className="border border-[#e4e9ec] px-8 py-3 text-sm transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c]">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function JobsSection({ token, onLogout }) {
  const [jobs, setJobs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Professional Sales", region: "", description: "", compensation: "" });

  const input = "w-full border border-[#e4e9ec] px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#0e8c8c]";
  const label = "font-lab mb-1.5 block text-[9px] uppercase tracking-[0.16em] text-[#56636b]";

  const load = () =>
    fetch(`${API}/api/admin/jobs`, { headers: authHeaders(token) })
      .then((r) => r.json())
      .then((d) => setJobs(Array.isArray(d) ? d : []))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/jobs`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify(form),
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      toast.success("Job posted — live on the careers page");
      setForm({ title: "", category: "Professional Sales", region: "", description: "", compensation: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/admin/jobs/${id}`, {
        method: "PUT",
        headers: authHeaders(token),
        body: JSON.stringify({ status }),
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      toast.success(
        status === "filled" ? "Marked as filled"
          : status === "hidden" ? "Hidden from the careers page"
            : "Reopened — accepting applications"
      );
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const saveEdit = async (id, fields) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/jobs/${id}`, {
        method: "PUT",
        headers: authHeaders(token),
        body: JSON.stringify(fields),
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      toast.success("Changes saved — live on the careers page");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Remove "${title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/jobs/${id}`, { method: "DELETE", headers: authHeaders(token) });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      toast.success("Job removed");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="mt-12 border border-[#e4e9ec] bg-white p-8" data-testid="admin-jobs-section">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Careers</p>
          <h2 className="font-display mt-2 text-3xl tracking-tight">Job postings</h2>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} data-testid="admin-jobs-toggle-form"
          className="inline-flex items-center gap-2 bg-[#0a0a0a] px-5 py-3 text-xs font-medium text-white transition-colors hover:bg-[#0e8c8c]">
          <Plus className="h-4 w-4" /> New posting
        </button>
      </div>

      {showAdd && (
        <form onSubmit={create} className="mt-6 border border-[#e4e9ec] bg-[#f7f9f9]/60 p-6" data-testid="admin-jobs-add-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className={label}>Job title *</label>
              <input required minLength={3} className={input} placeholder="Regional Sales Representative — Quebec" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-job-title" /></div>
            <div><label className={label}>Category *</label>
              <select className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-job-category">
                <option>Professional Sales</option>
                <option>Laboratory Assistant</option>
                <option>Warehouse &amp; Logistics</option>
                <option>Legal &amp; Compliance</option>
                <option>Finance &amp; Accounting</option>
              </select></div>
            <div><label className={label}>Region *</label>
              <input required className={input} placeholder="Quebec, Canada" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="admin-job-region" /></div>
            <div><label className={label}>Compensation</label>
              <input className={input} placeholder="e.g. Base salary + commission" value={form.compensation || ""} onChange={(e) => setForm({ ...form, compensation: e.target.value })} data-testid="admin-job-compensation" /></div>
            <div className="sm:col-span-2"><label className={label}>Description *</label>
              <textarea required rows={3} minLength={10} className={input} placeholder="What the role involves and who should apply…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-job-description" /></div>
          </div>
          <p className="font-lab mt-3 text-[9px] text-[#8a959c]">Posting timestamp is set automatically · new postings open as “Accepting applications”.</p>
          <button type="submit" disabled={busy} data-testid="admin-jobs-submit"
            className="mt-4 bg-[#0e8c8c] px-8 py-3 text-sm font-medium text-white transition-colors hover:bg-[#0a0a0a] disabled:opacity-50">
            {busy ? "Posting…" : "Post job"}
          </button>
        </form>
      )}

      <div className="mt-6">
        {jobs.length === 0 && <p className="text-sm text-[#56636b]" data-testid="admin-jobs-empty">No job postings yet — create the first one above.</p>}
        {jobs.map((j) => (
          <div key={j.id} className="border-b border-[#e4e9ec] py-4 last:border-b-0" data-testid={`admin-job-row-${j.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{j.title}</p>
                <p className="font-lab mt-0.5 text-[9px] uppercase tracking-[0.14em] text-[#56636b]">
                  {j.category} · {j.region} · {JOB_STATUS_LABEL[j.status] || j.status.toUpperCase()}{j.compensation ? ` · ${j.compensation}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setEditingId(editingId === j.id ? null : j.id)} data-testid={`admin-job-edit-${j.id}`}
                  className="inline-flex items-center gap-1.5 border border-[#e4e9ec] px-4 py-2.5 text-xs transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c]">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                {j.status === "open" ? (
                  <>
                    <button onClick={() => setStatus(j.id, "filled")} data-testid={`admin-job-fill-${j.id}`}
                      className="inline-flex items-center gap-1.5 border border-[#e4e9ec] px-4 py-2.5 text-xs transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c]">
                      <Briefcase className="h-3.5 w-3.5" /> Mark as filled
                    </button>
                    <button onClick={() => setStatus(j.id, "hidden")} data-testid={`admin-job-hide-${j.id}`}
                      className="inline-flex items-center gap-1.5 border border-[#e4e9ec] px-4 py-2.5 text-xs transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c]">
                      <EyeOff className="h-3.5 w-3.5" /> Hide
                    </button>
                  </>
                ) : (
                  <button onClick={() => setStatus(j.id, "open")} data-testid={`admin-job-reopen-${j.id}`}
                    className="inline-flex items-center gap-1.5 border border-[#e4e9ec] px-4 py-2.5 text-xs transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c]">
                    <RotateCcw className="h-3.5 w-3.5" /> Reopen
                  </button>
                )}
                <button onClick={() => remove(j.id, j.title)} data-testid={`admin-job-delete-${j.id}`}
                  className="inline-flex items-center gap-1.5 border border-[#e4e9ec] px-4 py-2.5 text-xs text-[#b3261e] transition-colors hover:border-[#b3261e]">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            {editingId === j.id && (
              <JobRowForm
                testPrefix={`admin-job-edit-${j.id}`}
                initial={{ title: j.title, category: j.category, region: j.region, description: j.description, compensation: j.compensation || "", status: j.status }}
                busy={busy}
                submitLabel="Save changes"
                onSubmit={(fields) => saveEdit(j.id, fields)}
                onCancel={() => setEditingId(null)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [products, setProducts] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", sku: "", size: "", potency: "", application: "", form: "", category: "Home Care", price: "", stock: "", description: "", image: "" });

  const load = async () => {
    const res = await fetch(`${API}/api/products`);
    const data = await res.json();
    setProducts(data);
    setDrafts(Object.fromEntries(data.map((p) => [p.slug, { price: (p.price_cents / 100).toString(), stock: String(p.available), sku: p.sku || "" }])));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (slug) => {
    const d = drafts[slug];
    try {
      const res = await fetch(`${API}/api/admin/products/${slug}`, {
        method: "PUT",
        headers: authHeaders(token),
        body: JSON.stringify({ sku: d.sku?.trim() || undefined, price_cents: Math.round(parseFloat(d.price) * 100), available: parseInt(d.stock || "0", 10) }),
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      toast.success(`${data.name} updated`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (slug, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    const res = await fetch(`${API}/api/admin/products/${slug}`, { method: "DELETE", headers: authHeaders(token) });
    if (res.status === 401) { onLogout(); return; }
    toast.success("Product removed");
    load();
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/admin/products`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({
          name: newProduct.name,
          sku: newProduct.sku,
          size: newProduct.size,
          potency: newProduct.potency,
          application: newProduct.application,
          form: newProduct.form,
          category: newProduct.category,
          description: newProduct.description,
          image: newProduct.image || undefined,
          price_cents: Math.round(parseFloat(newProduct.price) * 100),
          available: parseInt(newProduct.stock || "0", 10),
        }),
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      toast.success(`${data.name} added to the collection`);
      setShowAdd(false);
      setNewProduct({ name: "", sku: "", size: "", potency: "", application: "", form: "", category: "Home Care", price: "", stock: "", description: "", image: "" });
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/admin/sync-inventory`, { method: "POST", headers: authHeaders(token) });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      if (data.updated && data.updated.length) {
        toast.success(`Sheet synced — ${data.updated.length} product(s) updated`);
      } else {
        toast.success("Sheet synced — prices and stock already up to date");
      }
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const input = "w-full border border-[#e4e9ec] px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#0e8c8c]";
  const label = "font-lab mb-1.5 block text-[9px] uppercase tracking-[0.16em] text-[#56636b]";

  return (
    <div className="mx-auto max-w-[1200px] px-5 pb-24 pt-28 md:px-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Exoplus Bio · Admin</p>
          <h1 className="font-display mt-2 text-4xl tracking-tight">Pricing & products</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={syncNow} disabled={syncing} data-testid="admin-sync-inventory-button"
            className="inline-flex items-center gap-2 border border-[#e4e9ec] px-5 py-3 text-xs font-medium transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c] disabled:opacity-50">
            {syncing ? "Syncing…" : "Sync sheet now"}
          </button>
          <button onClick={() => setShowAdd(!showAdd)} data-testid="admin-toggle-add-form"
            className="inline-flex items-center gap-2 bg-[#0a0a0a] px-5 py-3 text-xs font-medium text-white transition-colors hover:bg-[#0e8c8c]">
            <Plus className="h-4 w-4" /> New product
          </button>
          <button onClick={() => { localStorage.removeItem(TOKEN_KEY); onLogout(); }} data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 border border-[#e4e9ec] px-5 py-3 text-xs font-medium transition-colors hover:border-[#0a0a0a]">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={add} className="mt-10 border border-[#e4e9ec] bg-white p-8" data-testid="admin-add-product-form">
          <h2 className="font-display text-2xl">Add a new product line</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2"><label className={label}>Product name *</label>
              <input required className={input} value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} data-testid="admin-new-name" /></div>
            <div><label className={label}>SKU</label>
              <input className={input} value={newProduct.sku} onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })} data-testid="admin-new-sku" /></div>
            <div><label className={label}>Size</label>
              <input className={input} placeholder="40 mL" value={newProduct.size} onChange={(e) => setNewProduct({ ...newProduct, size: e.target.value })} data-testid="admin-new-size" /></div>
            <div><label className={label}>Exosome count</label>
              <input className={input} placeholder="60 Billion — Plant" value={newProduct.potency} onChange={(e) => setNewProduct({ ...newProduct, potency: e.target.value })} data-testid="admin-new-potency" /></div>
            <div><label className={label}>Application</label>
              <input className={input} placeholder="Topical / Nano / Air" value={newProduct.application} onChange={(e) => setNewProduct({ ...newProduct, application: e.target.value })} data-testid="admin-new-application" /></div>
            <div><label className={label}>Format</label>
              <input className={input} placeholder="Liquid" value={newProduct.form} onChange={(e) => setNewProduct({ ...newProduct, form: e.target.value })} data-testid="admin-new-form" /></div>
            <div><label className={label}>Category</label>
              <select className={input} value={newProduct.category} onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })} data-testid="admin-new-category">
                <option>Home Care</option><option>Professional</option>
              </select></div>
            <div><label className={label}>Suggested retail price (CAD) *</label>
              <input required type="number" step="0.01" min="0.01" className={input} value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} data-testid="admin-new-price" /></div>
            <div><label className={label}>Stock qty</label>
              <input type="number" min="0" className={input} value={newProduct.stock} onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })} data-testid="admin-new-stock" /></div>
            <div className="sm:col-span-3"><label className={label}>Image URL (optional — a clinical placeholder is used if empty)</label>
              <input className={input} value={newProduct.image} onChange={(e) => setNewProduct({ ...newProduct, image: e.target.value })} data-testid="admin-new-image" /></div>
            <div className="sm:col-span-3"><label className={label}>Description</label>
              <textarea rows={3} className={input} value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} data-testid="admin-new-description" /></div>
          </div>
          <button type="submit" data-testid="admin-add-product-submit"
            className="mt-6 bg-[#0e8c8c] px-8 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#0a0a0a]">
            Add product
          </button>
        </form>
      )}

      <div className="mt-10 border border-[#e4e9ec]">
        <div className="hidden grid-cols-12 gap-4 border-b border-[#e4e9ec] bg-[#f4f3ef] px-5 py-3 md:grid">
          <span className="font-lab col-span-4 text-[9px] uppercase tracking-[0.16em] text-[#56636b]">Product</span>
          <span className="font-lab col-span-2 text-[9px] uppercase tracking-[0.16em] text-[#56636b]">SKU</span>
          <span className="font-lab col-span-2 text-[9px] uppercase tracking-[0.16em] text-[#56636b]">Suggested retail (CAD)</span>
          <span className="font-lab col-span-2 text-[9px] uppercase tracking-[0.16em] text-[#56636b]">Stock</span>
          <span className="font-lab col-span-2 text-right text-[9px] uppercase tracking-[0.16em] text-[#56636b]">Actions</span>
        </div>
        {products === null && <p className="font-lab px-5 py-6 text-xs text-[#56636b]">Loading…</p>}
        {products && products.length === 0 && <p className="px-5 py-6 text-sm text-[#56636b]">No products yet — add your first product line above.</p>}
        {products && products.map((p) => (
          <div key={p.slug} className="grid grid-cols-1 items-center gap-4 border-b border-[#e4e9ec] px-5 py-4 last:border-b-0 md:grid-cols-12" data-testid={`admin-product-row-${p.slug}`}>
            <div className="col-span-4 flex items-center gap-3">
              <img src={p.image} alt="" className="h-12 w-10 border border-[#e4e9ec] object-cover" />
              <div>
                <p className="text-sm font-medium">{p.name}</p>
                <p className="font-lab text-[9px] uppercase tracking-[0.14em] text-[#56636b]">{p.category}</p>
              </div>
            </div>
            <div className="col-span-2">
              <label className={`${label} md:hidden`}>SKU</label>
              <input className={input} value={drafts[p.slug]?.sku ?? ""} onChange={(e) => setDrafts({ ...drafts, [p.slug]: { ...drafts[p.slug], sku: e.target.value } })} data-testid={`admin-sku-input-${p.slug}`} />
            </div>
            <div className="col-span-2">
              <label className={`${label} md:hidden`}>Suggested retail (CAD)</label>
              <input className={input} value={drafts[p.slug]?.price ?? ""} onChange={(e) => setDrafts({ ...drafts, [p.slug]: { ...drafts[p.slug], price: e.target.value } })} data-testid={`admin-price-input-${p.slug}`} />
            </div>
            <div className="col-span-2">
              <label className={`${label} md:hidden`}>Stock</label>
              <input className={input} value={drafts[p.slug]?.stock ?? ""} onChange={(e) => setDrafts({ ...drafts, [p.slug]: { ...drafts[p.slug], stock: e.target.value } })} data-testid={`admin-stock-input-${p.slug}`} />
            </div>
            <div className="col-span-2 flex gap-2 md:justify-end">
              <button onClick={() => save(p.slug)} data-testid={`admin-save-${p.slug}`}
                className="inline-flex items-center gap-1.5 border border-[#e4e9ec] px-4 py-2.5 text-xs transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c]">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
              <button onClick={() => remove(p.slug, p.name)} data-testid={`admin-delete-${p.slug}`}
                className="inline-flex items-center gap-1.5 border border-[#e4e9ec] px-4 py-2.5 text-xs text-[#b3261e] transition-colors hover:border-[#b3261e]">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <TeamSection token={token} />
      <JobsSection token={token} onLogout={onLogout} />
      <p className="font-lab mt-6 text-[10px] uppercase tracking-[0.14em] text-[#56636b]">
        Changes publish to the storefront instantly · Square checkout uses the CAD price here
      </p>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  return token ? <Dashboard token={token} onLogout={() => setToken(null)} /> : <Login onLogin={setToken} />;
}